import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heading1, Heading2, Quote, List, Bold, Save, ArrowLeft, BookOpen, ClipboardCheck, AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { RelationPicker } from "@/components/admin/RelationPicker";
import { ArticleRenderer } from "@/components/collection";
import { formatCitation } from "@/lib/article";
import { analyzeArticle } from "@/lib/writingAssistant";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const SAVE_KEYS = [
  "slug", "title", "category", "short_description", "historical_context", "provenance_summary",
  "provenance_status", "images", "psa_status", "psa_submission_date", "psa_return_date",
  "psa_grade", "psa_cert_number", "psa_notes", "graceland_displayed", "graceland_details",
  "related_item_ids", "citation_ids", "research_doc_ids", "related_artifact_ids",
  "provenance_event_ids", "references", "article_markdown", "featured", "order",
];

export const AdminStoryBuilderPage = () => {
  const { id } = useParams();
  const { password } = useAdmin();
  const headers = { "x-admin-password": password };
  const [form, setForm] = useState(null);
  const [allCitations, setAllCitations] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const taRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [itemsRes, citRes, evRes] = await Promise.all([
        api.get("/admin/collection/items", { headers }),
        api.get("/admin/citations", { headers }),
        api.get("/admin/collection/timeline", { headers }),
      ]);
      const it = itemsRes.data.find((x) => x.id === id);
      setAllItems(itemsRes.data || []);
      setAllCitations(citRes.data || []);
      setAllEvents(evRes.data || []);
      if (it) setForm({ ...it, article_markdown: it.article_markdown || "" });
    } catch {
      toast.error("Failed to load item");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, password]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((d) => ({ ...d, [k]: v }));

  const attachedCitations = (form?.citation_ids || [])
    .map((cid) => allCitations.find((c) => c.id === cid))
    .filter(Boolean);

  const insertAtCursor = (before, after = "", placeholder = "") => {
    const ta = taRef.current;
    const val = form.article_markdown || "";
    if (!ta) { set("article_markdown", val + before + placeholder + after); return; }
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = val.slice(s, e) || placeholder;
    const next = val.slice(0, s) + before + sel + after + val.slice(e);
    set("article_markdown", next);
    setTimeout(() => {
      ta.focus();
      const pos = s + before.length + sel.length + after.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      SAVE_KEYS.forEach((k) => { payload[k] = form[k]; });
      payload.order = Number(form.order) || 0;
      await api.put(`/admin/collection/items/${id}`, payload, { headers });
      toast.success("Story saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (!form) return <div className="p-8"><p className="text-gray-500">Item not found.</p><Link to="/admin/collection-items" className="text-blue-600">← Back</Link></div>;

  const linkedEvents = allEvents.filter((e) => (form.provenance_event_ids || []).includes(e.id));
  const analysis = analyzeArticle({
    markdown: form.article_markdown,
    citations: attachedCitations,
    item: form,
    allItems,
    events: linkedEvents,
  });
  const warnings = analysis.issues.filter((i) => i.severity === "warning");
  const infos = analysis.issues.filter((i) => i.severity === "info");

  return (
    <div data-testid="admin-story-builder">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin/collection-items" className="text-xs text-gray-500 hover:text-blue-600 inline-flex items-center gap-1 mb-1"><ArrowLeft className="w-3 h-3" /> Collection Items</Link>
          <h1 className="text-2xl font-bold serif-heading flex items-center gap-2"><BookOpen className="w-6 h-6" /> Story Builder</h1>
          <p className="text-sm text-gray-500">{form.title}</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="story-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Story"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor column */}
        <div className="space-y-4">
          <div className="bg-white rounded border p-4 space-y-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-gray-500">Structured Content</h2>
            <div><Label>Historical Context</Label><Textarea value={form.historical_context || ""} onChange={(e) => set("historical_context", e.target.value)} rows={3} className="mt-1" /></div>
            <div><Label>Provenance Narrative</Label><Textarea value={form.provenance_summary || ""} onChange={(e) => set("provenance_summary", e.target.value)} rows={3} className="mt-1" /></div>
            {form.graceland_displayed && (
              <div><Label>Graceland Record</Label><Textarea value={form.graceland_details || ""} onChange={(e) => set("graceland_details", e.target.value)} rows={2} className="mt-1" /></div>
            )}
          </div>

          <div className="bg-white rounded border p-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-gray-500 mb-3">Relationships</h2>
            <div className="grid gap-3">
              <RelationPicker label="Citations" endpoint="/admin/citations" selected={form.citation_ids || []} onChange={(v) => set("citation_ids", v)} headers={headers} labelKey="title" subKeys={["source_type", "publisher"]} />
              <RelationPicker label="Research Documents" endpoint="/admin/research" selected={form.research_doc_ids || []} onChange={(v) => set("research_doc_ids", v)} headers={headers} labelKey="title" subKeys={["doc_type", "date"]} />
              <RelationPicker label="Related Items" endpoint="/admin/collection/items" selected={form.related_item_ids || []} onChange={(v) => set("related_item_ids", v)} headers={headers} labelKey="title" subKeys={["category"]} excludeId={id} />
              <RelationPicker label="Related Artifacts" endpoint="/admin/artifacts" selected={form.related_artifact_ids || []} onChange={(v) => set("related_artifact_ids", v)} headers={headers} labelKey="name" subKeys={["category"]} />
              <RelationPicker label="Timeline / Provenance Events" endpoint="/admin/collection/timeline" selected={form.provenance_event_ids || []} onChange={(v) => set("provenance_event_ids", v)} headers={headers} labelKey="title" subKeys={["date_label", "phase"]} />
            </div>
          </div>

          <div className="bg-white rounded border p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-sm uppercase tracking-wide text-gray-500">Long-form Article</h2>
            </div>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 mb-2 border-b pb-2">
              <button type="button" onClick={() => insertAtCursor("\n## ", "", "Heading")} className="p-1.5 hover:bg-gray-100 rounded" title="Heading"><Heading1 className="w-4 h-4" /></button>
              <button type="button" onClick={() => insertAtCursor("\n### ", "", "Subheading")} className="p-1.5 hover:bg-gray-100 rounded" title="Subheading"><Heading2 className="w-4 h-4" /></button>
              <button type="button" onClick={() => insertAtCursor("\n> ", "", "Quote")} className="p-1.5 hover:bg-gray-100 rounded" title="Block quote"><Quote className="w-4 h-4" /></button>
              <button type="button" onClick={() => insertAtCursor("\n- ", "", "List item")} className="p-1.5 hover:bg-gray-100 rounded" title="Bullet list"><List className="w-4 h-4" /></button>
              <button type="button" onClick={() => insertAtCursor("**", "**", "bold")} className="p-1.5 hover:bg-gray-100 rounded" title="Bold"><Bold className="w-4 h-4" /></button>
              <div className="ml-auto">
                <select
                  className="border rounded px-2 py-1 text-xs"
                  value=""
                  onChange={(e) => { if (e.target.value) { insertAtCursor(`[^c:${e.target.value}]`); e.target.value = ""; } }}
                  data-testid="insert-citation-select"
                >
                  <option value="">Insert citation…</option>
                  {attachedCitations.map((c) => (
                    <option key={c.id} value={c.id}>{(c.title || "Untitled").slice(0, 50)}</option>
                  ))}
                </select>
              </div>
            </div>
            <Textarea
              ref={taRef}
              value={form.article_markdown || ""}
              onChange={(e) => set("article_markdown", e.target.value)}
              rows={18}
              className="font-mono text-sm"
              placeholder="Write the museum-style article in Markdown. Use the toolbar for headings, quotes, lists, and to insert citations."
              data-testid="article-editor"
            />
            <p className="text-xs text-gray-400 mt-2">
              Markdown: <code># Heading</code>, <code>## Sub</code>, <code>&gt; quote</code>, <code>- list</code>, <code>**bold**</code>, <code>*italic*</code>, <code>[text](url)</code>. Citations insert as <code>[^c:ID]</code> and render as numbered footnotes.
            </p>
          </div>
        </div>

        {/* Preview column */}
        <div className="lg:sticky lg:top-6 self-start space-y-4">
          <div className="bg-[#F5F4F0] rounded border p-6 max-h-[70vh] overflow-auto">
            <div className="text-xs uppercase tracking-wide text-[#8C271E] font-semibold mb-2">Live Preview</div>
            <h1 className="showcase-serif text-2xl mb-2">{form.title}</h1>
            {form.article_markdown ? (
              <ArticleRenderer markdown={form.article_markdown} citations={attachedCitations} />
            ) : (
              <p className="text-sm text-gray-400">The article preview will appear here as you write.</p>
            )}
            {attachedCitations.length > 0 && !form.article_markdown && (
              <div className="mt-4 text-xs text-gray-500">
                {attachedCitations.length} citation(s) attached — insert them into the article to create footnotes.
              </div>
            )}
          </div>

          {/* Writing Assistant */}
          <div className="bg-white rounded border p-4" data-testid="writing-assistant">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-sm uppercase tracking-wide text-gray-500 flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" /> Writing Assistant
              </h2>
              <span className="text-xs text-gray-500">{analysis.stats.words} words · {analysis.stats.sections} sections</span>
            </div>
            {analysis.issues.length === 0 ? (
              <p className="text-sm text-green-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> No editorial issues detected.</p>
            ) : (
              <ul className="space-y-2" data-testid="wa-issues">
                {warnings.map((iss, i) => (
                  <li key={`w${i}`} className="flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                    <span>{iss.message}</span>
                  </li>
                ))}
                {infos.map((iss, i) => (
                  <li key={`i${i}`} className="flex items-start gap-2 text-sm text-gray-600">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-400" />
                    <span>{iss.message}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-gray-400 mt-3">Editorial checks only — nothing is changed automatically.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStoryBuilderPage;
